import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { toast } from '@artic/ui/toast';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import {
  changeControlRunItemsQueryOptions,
  changeControlRunQueryOptions,
  useResumeRun,
} from '../../features/change-control/queries';
import { RunProgress } from '../../features/change-control/run-progress';
import type { ChangeControlRunStatus } from '../../features/change-control/types';

export const Route = createFileRoute('/change_control/$run_id')({
  staticData: {
    breadcrumb: 'Run',
    layout: 'default',
  },
  loader: ({ context: { queryClient }, params: { run_id: runId } }) =>
    Promise.all([
      queryClient.ensureQueryData(changeControlRunQueryOptions(runId)),
      queryClient.ensureQueryData(changeControlRunItemsQueryOptions(runId)),
    ]),
  pendingComponent: () => (
    <div className="p-6">
      <p className="text-muted-foreground text-sm">Loading run…</p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-6">
      <p className="text-destructive text-sm">
        Failed to load run: {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </div>
  ),
  component: RunDetailPage,
});

const ITEM_STATUS_VARIANT: Record<
  ChangeControlRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  queued: 'secondary',
  processing: 'default',
  completed: 'default',
  partially_failed: 'destructive',
  failed: 'destructive',
};

function RunDetailPage() {
  const { run_id: runId } = Route.useParams();
  const live = useFeatureFlag('quality_realtime');
  const { data: run } = useSuspenseQuery(changeControlRunQueryOptions(runId, { live }));
  const { data: items } = useSuspenseQuery(changeControlRunItemsQueryOptions(runId));
  const resumeMutation = useResumeRun();

  const handleResume = async () => {
    try {
      const result = await resumeMutation.mutateAsync(runId);
      toast.success(
        result.requeued > 0
          ? `Re-queued ${result.requeued} ${result.requeued === 1 ? 'item' : 'items'}`
          : 'Nothing to resume',
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Resume failed: ${message}`);
    }
  };

  // Resume is offered for any non-terminal run that hasn't moved (queued
  // or processing) plus the terminal-but-not-everything-succeeded states.
  // Useful when the BullMQ worker wasn't running at start time.
  const canResume =
    run.status === 'partially_failed' ||
    run.status === 'failed' ||
    run.status === 'queued' ||
    run.status === 'processing';
  const resumeLabel =
    run.status === 'queued' || run.status === 'processing'
      ? 'Re-enqueue stuck items'
      : 'Resume failed items';

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Change Control Run</h1>
          <p className="text-muted-foreground text-sm">
            {run.kind === 'bill_of_operations_release' ? 'Bill of Operations' : 'Operation'} release
            · target version v{run.target_version}
          </p>
          <p className="text-muted-foreground mt-1 font-mono text-xs">
            {run.change_control_run_id}
          </p>
        </div>
        {canResume && (
          <Button onClick={handleResume} disabled={resumeMutation.isPending}>
            {resumeMutation.isPending ? 'Resuming…' : resumeLabel}
          </Button>
        )}
      </header>

      <RunProgress runId={runId} className="border-border rounded border p-4" />

      <section>
        <h2 className="mb-2 text-lg font-medium">Items</h2>
        {items.length > 0 ? (
          <div className="border-border overflow-hidden rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Work order</th>
                  <th className="px-3 py-2 font-medium">Target version</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Attempts</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.change_control_run_item_id} className="border-border border-t">
                    <td className="px-3 py-2 font-mono text-xs">{item.work_order_id}</td>
                    <td className="px-3 py-2 tabular-nums">v{item.bill_of_operations_version}</td>
                    <td className="px-3 py-2">
                      <Badge variant={ITEM_STATUS_VARIANT[item.status]}>{item.status}</Badge>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{item.attempts}</td>
                    <td className="text-muted-foreground px-3 py-2">
                      {item.error ?? <span className="opacity-50">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No items.</p>
        )}
      </section>
    </div>
  );
}
